/**
 * useManualBet React Hook
 *
 * This hook manages the manual bet entry functionality for the Lotto UI. It handles bet type selection,
 * number input, and integrates with the bet control system. The hook is designed to work with the
 * LottoContext and provides a clean API for the manual entry interface.
 *
 * @param {Function} addLottoListAndHandleResponse - The function from useBetControl to handle bet submissions
 *
 * @returns {Object} An object containing:
 * - betTypes: Array of available bet type options that can be selected
 * - selectedBetTypes: Currently selected bet type(s)
 * - handleSelectBetType: Function to select a bet type
 * - inputDigit: Number of digits required for the current bet type
 * - handleSelectBetNumber: Function to handle number selection for the bet
 *
 * @example
 * ```tsx
 * import useManualBet from 'src/pagev2/game/hooks/useManualBet';
 * import useBetControl from '../useBetControl';
 *
 * function ManualEntry() {
 *   const { addLottoListAndHandleResponse } = useBetControl();
 *   const {
 *     betTypes,
 *     selectedBetTypes,
 *     handleSelectBetType,
 *     inputDigit,
 *     handleSelectBetNumber,
 *   } = useManualBet(addLottoListAndHandleResponse);
 *
 *   // Use these in your component
 * }
 * ```
 *
 * This hook is used in the manual entry interface for lotto games, working with the bet control system.
 */
import { useSelectBetType } from './useSelectBetType'
import { useRenderItem } from './useRenderItem'
import { useSelectBetNumber } from './useSelectBetNumber'
import { BetType } from './types'
import { isAllowToRenderBetType } from './utils/useManualBet.utils'
import useBetControl from '../useBetControl'
import { BetTypeEnum } from '@/ui-components/components/BetLotto/BetLotto.types'
interface ManualBetHookReturnType {
  betTypes: { key: BetType; label: string }[]
  selectedBetTypes: BetType[]
  handleSelectBetType: (types: BetType) => void
  inputDigit: number
  handleSelectBetNumber: (number: string, types: BetType[]) => void
}

const useManualBet = (
  addLottoListAndHandleResponse: ReturnType<
    typeof useBetControl
  >['addLottoListAndHandleResponse'],
): ManualBetHookReturnType => {
  // TODO: need to add default of bet types with first of bet types
  const {
    selectedBetTypes,
    handleSelectBetType: handleSelectBetTypeInternal,
    inputDigit,
  } = useSelectBetType([BetTypeEnum.TWO_TOP])
  const { renderItem } = useRenderItem(
    selectedBetTypes,
    addLottoListAndHandleResponse,
  )
  const { handleSelectBetNumber } = useSelectBetNumber(
    addLottoListAndHandleResponse,
  )

  const handleSelectBetType = (type: BetType) => {
    const betType = renderItem.find((item) => item.key === type)

    if (betType?.onClick) {
      betType.onClick()
      return
    }

    handleSelectBetTypeInternal(type)
  }

  const betTypes = renderItem.filter((item) =>
    isAllowToRenderBetType(item.key, selectedBetTypes),
  )

  return {
    betTypes,
    selectedBetTypes,
    handleSelectBetType,
    inputDigit,
    handleSelectBetNumber,
  }
}

export default useManualBet
